import { useCallback, useEffect, useState } from "react";

/**
 * PIN entry.
 *
 * Touch-first by construction: every key is at least --touch-primary, there is
 * no hover affordance, and the whole flow is completable with no keyboard
 * (FR-089, FR-090). Physical keys are accepted as a convenience for desktop
 * back-office use, never as a requirement.
 */

interface PinPadProps {
  length?: number;
  label?: string;
  error?: string | null;
  busy?: boolean;
  onSubmit: (pin: string) => void;
  onCancel?: () => void;
}

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

export function PinPad({
  length = 4,
  label = "Enter your PIN",
  error = null,
  busy = false,
  onSubmit,
  onCancel,
}: PinPadProps) {
  const [pin, setPin] = useState("");

  // Clearing on a fresh error stops staff re-submitting the same wrong PIN and
  // burning another attempt against the lockout counter.
  useEffect(() => {
    if (error) setPin("");
  }, [error]);

  const press = useCallback(
    (digit: string) => {
      if (busy) return;
      setPin((current) => {
        const next = (current + digit).slice(0, length);
        if (next.length === length) {
          // Defer so the final dot paints before the request begins.
          queueMicrotask(() => onSubmit(next));
        }
        return next;
      });
    },
    [busy, length, onSubmit],
  );

  const backspace = useCallback(() => {
    if (busy) return;
    setPin((current) => current.slice(0, -1));
  }, [busy]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key >= "0" && event.key <= "9") press(event.key);
      else if (event.key === "Backspace") backspace();
      else if (event.key === "Escape") onCancel?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [press, backspace, onCancel]);

  return (
    <div className="pinpad">
      <p className="pinpad__label">{label}</p>

      <div className="pinpad__dots" role="status" aria-label={`${pin.length} of ${length} digits`}>
        {Array.from({ length }, (_, i) => (
          <span key={i} className={`pinpad__dot${i < pin.length ? " is-filled" : ""}`} />
        ))}
      </div>

      <p className={`pinpad__error${error ? " is-visible" : ""}`} role="alert">
        {error ?? " "}
      </p>

      <div className="pinpad__keys">
        {KEYS.map((key) => (
          <button key={key} type="button" className="pinpad__key" onClick={() => press(key)} disabled={busy}>
            {key}
          </button>
        ))}

        <button
          type="button"
          className="pinpad__key pinpad__key--muted"
          onClick={onCancel}
          disabled={busy || !onCancel}
        >
          {onCancel ? "Cancel" : ""}
        </button>

        <button type="button" className="pinpad__key" onClick={() => press("0")} disabled={busy}>
          0
        </button>

        <button
          type="button"
          className="pinpad__key pinpad__key--muted"
          onClick={backspace}
          disabled={busy}
          aria-label="Delete"
        >
          ⌫
        </button>
      </div>
    </div>
  );
}

export default PinPad;
